import { useCallback, useRef } from "react";
import { useQuery, useQueryClient, type QueryClient } from "react-query";
import CustomerApiService from "../../../CustomerApiService";

const PREFIX = "/ab-vault-tools";

export interface Option {
  label: string;
  value: string;
}
export type OptionMap = Record<string, Option[]>;

export interface NextEvent {
  catalogId: string;
  lastEventId: number | null;
  lastPartId: string | null;
  suggestedNext: number;
}

export type JobStatus =
  | "RESERVING"
  | "RESERVED"
  | "RUNNING"
  | "COMPLETED"
  | "PARTIAL"
  | "FAILED";
export type ItemStatus = "PENDING" | "CREATED" | "FAILED" | "SKIPPED_EXISTS";

export interface CreatedItem {
  rowRef: string;
  catalogId: string;
  eventId: number;
  partId: string;
  vaultId: string;
  fileName: string;
  correctedFromSuggested: boolean;
}

export interface JobCreated {
  jobId: string;
  status: JobStatus;
  reserved: { catalogId: string; firstEventId: number; count: number }[];
  items: CreatedItem[];
  gaps: Gap[];
}

export interface Gap {
  catalogId: string;
  eventId: number;
}

export interface JobItem {
  rowRef: string;
  catalogId: string;
  eventId: number;
  partId: string;
  vaultId: string;
  status: ItemStatus;
  assetId?: string;
  message?: string;
}

export interface Job {
  jobId: string;
  status: JobStatus;
  counts: {
    total: number;
    created: number;
    skipped: number;
    failed: number;
    pending: number;
  };
  gaps: Gap[];
  items: JobItem[];
}

export const isJobActive = (s?: JobStatus) =>
  s === "RESERVING" || s === "RESERVED" || s === "RUNNING";

/**
 * True once nothing more will change. FAILED/PARTIAL are final even with items still PENDING;
 * only a COMPLETED job can still be catching its counts up.
 */
export const isJobSettled = (job: Pick<Job, "status" | "counts">) =>
  !isJobActive(job.status) &&
  !(job.status === "COMPLETED" && job.counts.pending > 0);

export const vaultKeys = {
  options: ["ab-vault-tools", "options"] as const,
  nextEvent: (catalog: string) =>
    ["ab-vault-tools", "next-event", catalog] as const,
  job: (jobId: string) => ["ab-vault-tools", "job", jobId] as const,
  jobs: ["ab-vault-tools", "jobs"] as const,
  myPlaceholders: ["ab-vault-tools", "my-placeholders"] as const,
};

export const getFormOptions = () =>
  CustomerApiService.fetch<{ options: OptionMap }>(
    "GET",
    `${PREFIX}/form-options`
  );

export const getNextEvent = (catalogId: string) =>
  CustomerApiService.fetch<NextEvent>(
    "GET",
    `${PREFIX}/catalogs/${encodeURIComponent(catalogId)}/next-event`
  );

export const getJob = (jobId: string) =>
  CustomerApiService.fetch<Job>("GET", `${PREFIX}/jobs/${jobId}`);

export const getJobResults = (jobId: string) =>
  CustomerApiService.fetch<{ fileName: string; csv: string }>(
    "GET",
    `${PREFIX}/jobs/${jobId}/results`
  );

export const resumeJob = (jobId: string) =>
  CustomerApiService.fetch<{
    jobId: string;
    status: JobStatus;
    resumed: boolean;
  }>("POST", `${PREFIX}/jobs/${jobId}/resume`, {});

export const listJobs = () =>
  CustomerApiService.fetch<{
    jobs: {
      jobId: string;
      status: JobStatus;
      source: "single" | "bulk";
      itemCount: number;
      createdAt: string;
      updatedAt: string;
    }[];
    nextCursor: string | null;
  }>("GET", `${PREFIX}/jobs`);

export type PlaceholderState = "creating" | "failed" | "awaiting" | "finished";

export interface MyPlaceholderJob {
  jobId: string;
  source: "single" | "bulk";
  createdAt: string;
  items: {
    vaultId: string;
    state: PlaceholderState;
    message?: string;
  }[];
}

export const getMyPlaceholders = () =>
  CustomerApiService.fetch<{ jobs: MyPlaceholderJob[]; truncated: boolean }>(
    "GET",
    `${PREFIX}/my-placeholders`
  );

const CREATING_POLL_MS = 3000;
const AWAITING_POLL_MS = 30000;
/** A job is polled fast for this long after it was created, then backs off. */
const FAST_POLL_WINDOW_MS = 2 * 60_000;
/** A row still "creating" after this long is stuck, not live, so it no longer drives polling. */
const CREATING_STALE_MS = 30 * 60_000;

type PollData = { jobs: Pick<MyPlaceholderJob, "createdAt" | "items">[] };

const hasState = (data: PollData | undefined, state: PlaceholderState) =>
  !!data?.jobs.some((j) => j.items.some((i) => i.state === state));

/** Ages (ms) of the jobs that still have a row being created and are not stale. */
const liveCreatingAges = (data: PollData | undefined, now: number) =>
  (data?.jobs ?? [])
    .filter((j) => j.items.some((i) => i.state === "creating"))
    .map((j) => now - Date.parse(j.createdAt))
    .filter((age) => !(age >= CREATING_STALE_MS));

/**
 * 3s while a job is young and still creating, backing off to 30s as it ages and stopping
 * altogether for stuck rows; 30s while rows only wait for an upload.
 */
export const pollMs = (data?: PollData, now = Date.now()) => {
  const ages = liveCreatingAges(data, now);
  if (ages.some((age) => !(age >= FAST_POLL_WINDOW_MS))) {
    return CREATING_POLL_MS;
  }
  return ages.length > 0 || hasState(data, "awaiting")
    ? AWAITING_POLL_MS
    : false;
};

/** Polls until nothing is creating, then slowly while rows await their upload. */
export const useMyPlaceholders = () =>
  useQuery(vaultKeys.myPlaceholders, getMyPlaceholders, {
    refetchInterval: (data) => pollMs(data),
    staleTime: 30000,
  });

/** Refetch My Placeholders now; usable outside React (the upload session store). */
export const invalidateMyPlaceholders = (
  client: QueryClient = CustomerApiService.queryClient
) => void client.invalidateQueries(vaultKeys.myPlaceholders);

/** Call after a create or an upload completes so the list shows it without waiting for a poll. */
export const useRefreshMyPlaceholders = () => {
  const client = useQueryClient();
  return useCallback(() => invalidateMyPlaceholders(client), [client]);
};

export const useFormOptions = () =>
  useQuery(vaultKeys.options, getFormOptions, { staleTime: 60_000 });

export const useNextEvent = (catalogId?: string) =>
  useQuery(
    vaultKeys.nextEvent(catalogId ?? ""),
    () => getNextEvent(catalogId!),
    {
      enabled: !!catalogId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    }
  );

/**
 * Polls a job every 2s until it leaves RESERVED/RUNNING and its item counts have caught up.
 * On the running -> settled transition (once per job), My Placeholders is refetched so its
 * rows leave "Creating"; later focus refetches of a finished job don't. An observer whose first
 * fetch already shows the job settled never invalidates: refreshMine() after the create and the
 * 3s polling of young "Creating" rows already cover that case.
 */
export const useJob = (jobId?: string) => {
  const client = useQueryClient();
  const sawRunning = useRef(false);
  return useQuery(vaultKeys.job(jobId ?? ""), () => getJob(jobId!), {
    enabled: !!jobId,
    refetchInterval: (data) => (!data || !isJobSettled(data) ? 2000 : false),
    onSuccess: (job) => {
      if (!isJobSettled(job)) {
        sawRunning.current = true;
      } else if (sawRunning.current && markSettled(client, job.jobId)) {
        invalidateMyPlaceholders(client);
      }
    },
  });
};

const settledJobs = new WeakMap<QueryClient, Set<string>>();
/** True the first time a job is reported settled for this client, so observers don't repeat it. */
function markSettled(client: QueryClient, jobId: string) {
  const seen = settledJobs.get(client) ?? new Set<string>();
  settledJobs.set(client, seen);
  if (seen.has(jobId)) {
    return false;
  }
  seen.add(jobId);
  return true;
}

export const newId = (): string => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  const hex = (n: number) =>
    Array.from({ length: n }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`;
};

export function downloadText(
  fileName: string,
  text: string,
  type = "text/csv"
) {
  const url = URL.createObjectURL(
    new Blob([text], { type: `${type};charset=utf-8` })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Readable text for a message that may be a raw JSON error body, e.g. {"error":"..."} or
 * {"error":{"message":"..."}}. Anything else comes back trimmed.
 */
export function cleanMessage(raw: string): string {
  const text = raw.trim();
  if (!/^[{[]/.test(text)) {
    return text;
  }
  const find = (v: unknown): string | undefined => {
    if (typeof v === "string") {
      return v;
    }
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      return find(o.message) ?? find(o.error);
    }
    return undefined;
  };
  try {
    return find(JSON.parse(text))?.trim() || "Something went wrong";
  } catch {
    return text;
  }
}

/** Message to show for a failed call: the API's own text, or a short default. */
export const errorText = (e: unknown): string =>
  (e instanceof Error && e.message && cleanMessage(e.message)) ||
  "Something went wrong";
